/**
 * Loading analysis: contact allocation, conflict detection and resolution,
 * mission SLA, antenna utilisation, recommendations and network saturation.
 *
 * Ported from loader-conflicts.js (docs/appendix-A-loader-port-manifest.md,
 * section 10). Steps 1 to 11 keep the original arithmetic; the per-conflict
 * resolution (strategies a to e) replaces the original's two-way choice, see
 * PORT_NOTES.md. Pure: time, randomness and the SGP4 window source are injected.
 */
import type { Antenna, ContactWindow, ContactWindowMap, Mission, SlaStatus } from '@/features/loader/types';
import { ANALYSIS, RESOLUTION, type ResolutionStrategy } from '@/features/loader/constants';
import type {
  Allocation,
  AllocationMap,
  AnalysisResults,
  AnalysisSummary,
  AntennaSchedule,
  AntennaUtil,
  Conflict,
  DataSla,
  MissionSla,
  Recommendation,
  Resolution,
  Saturation,
  UtilStatus,
} from './types';
import { generateDeepSpaceWindows } from './syntheticWindows';
import { indexRecoverableTime, type RecoverableTime } from './recoverableTime';

export type ProgressCallback = (progress: { percent: number; message: string }) => void;

/** SGP4 contact windows for TLE missions only; the caller wires the worker. */
export type ComputeContactWindows = (
  missions: Mission[],
  antennas: Antenna[],
  startMs: number,
  endMs: number,
  onProgress?: ProgressCallback,
) => Promise<ContactWindowMap>;

export interface WeatherImpactInput {
  impactFactor: number;
  source: string;
}

export interface WeatherInput {
  live: boolean;
  /** antennaId -> impact. Entries with source 'static' are ignored. */
  impacts: Record<string, WeatherImpactInput>;
}

export interface AnalysisInput {
  missions: Mission[];
  antennas: Antenna[];
  startMs: number;
  endMs: number;
  /** Feature toggle registry; unknown ids resolve to true in the app registry. */
  isToggleEnabled: (id: string) => boolean;
  weather?: WeatherInput;
  computeContactWindows: ComputeContactWindows;
  random?: () => number;
  nowMs?: () => number;
  onProgress?: ProgressCallback;
  /** Browser runtime can isolate allocation work while pure callers remain synchronous. */
  allocate?: (input: AllocationInput) => Promise<AnalysisResults>;
}

export interface WindowSourceInput {
  missions: Mission[];
  antennas: Antenna[];
  startMs: number;
  endMs: number;
  computeContactWindows: ComputeContactWindows;
  random: () => number;
  onProgress?: ProgressCallback;
}

const MINUTE_MS = 60_000;
const HOUR_MS = 3_600_000;

/** Run the full loading analysis. */
export async function runAnalysis(input: AnalysisInput): Promise<AnalysisResults> {
  const { missions, antennas, startMs, endMs, isToggleEnabled } = input;
  const random = input.random ?? Math.random;
  const nowMs = input.nowMs ?? Date.now;

  // Step 1: missions by priority (lower tier wins); stable, so equal tiers keep input order.
  const sortedMissions = missions.slice().sort((a, b) => a.priorityTier - b.priorityTier);

  // Step 2: contact windows (SGP4 for TLE missions, synthetic tracks for the rest).
  const contactWindows = await computeAllContactWindows({
    missions,
    antennas,
    startMs,
    endMs,
    computeContactWindows: input.computeContactWindows,
    random,
    onProgress: input.onProgress,
  });

  const allocationInput: AllocationInput = {
    sortedMissions,
    missions,
    antennas,
    contactWindows,
    startMs,
    endMs,
    isToggleEnabled,
    weather: input.weather,
    nowMs,
  };
  return input.allocate ? input.allocate(allocationInput) : runAllocation(allocationInput);
}

/**
 * Contact windows for every mission: TLE missions through the injected SGP4
 * source (TLE missions first, in the order the source returns them), then one
 * synthetic set per deep-space or TLE-less mission. Arrays are copied so the
 * filtering steps never mutate the source's result.
 */
export async function computeAllContactWindows(input: WindowSourceInput): Promise<ContactWindowMap> {
  input = { ...input, antennas: input.antennas.filter(a => !a.capabilities.monitoringOnly) };
  const tleMissions: Mission[] = [];
  const scheduleMissions: Mission[] = [];
  for (const mission of input.missions) {
    if (mission.orbit?.tle && mission.orbit.type !== 'deep_space') tleMissions.push(mission);
    else scheduleMissions.push(mission);
  }

  const result: ContactWindowMap = {};
  if (tleMissions.length > 0) {
    const computed = await input.computeContactWindows(
      tleMissions,
      input.antennas,
      input.startMs,
      input.endMs,
      input.onProgress,
    );
    for (const missionId of Object.keys(computed)) {
      result[missionId] = {};
      for (const antennaId of Object.keys(computed[missionId])) {
        result[missionId][antennaId] = computed[missionId][antennaId].slice();
      }
    }
  }

  for (const mission of scheduleMissions) {
    result[mission.id] = {};
    for (const antenna of input.antennas) {
      result[mission.id][antenna.id] = generateDeepSpaceWindows(
        mission,
        antenna,
        input.startMs,
        input.endMs,
        input.random,
      );
    }
  }
  return result;
}

export interface AllocationInput {
  sortedMissions: Mission[];
  missions: Mission[];
  antennas: Antenna[];
  contactWindows: ContactWindowMap;
  startMs: number;
  endMs: number;
  isToggleEnabled: (id: string) => boolean;
  weather?: WeatherInput;
  nowMs: () => number;
}

/** Steps 3 to 11 on contact windows that are already computed (synchronous). */
export function runAllocation(input: AllocationInput): AnalysisResults {
  const { sortedMissions, missions, antennas, contactWindows, startMs, endMs, isToggleEnabled } = input;

  // Step 3: weather degradation, only in live mode.
  if (input.weather?.live && input.weather.impacts) {
    applyWeatherDegradation(contactWindows, antennas, input.weather.impacts);
  }

  // Step 4: maintenance blocking (toggle maintenance_blocking).
  if (isToggleEnabled('maintenance_blocking')) {
    applyMaintenanceBlocking(contactWindows, antennas);
  }

  // Step 5: drop passes shorter than the mission's minimum.
  applyMinimumPass(contactWindows, missions, antennas);

  // Step 6: per-antenna sweep-line allocation.
  const capacityOf = (antenna: Antenna) => getAntennaCapacity(antenna, isToggleEnabled);
  const sweep = allocateByAntennaSweep(missions, antennas, contactWindows, capacityOf);

  const missionLookup = new Map(missions.map((m) => [m.id, m]));
  const antennaLookup = new Map(antennas.map((a) => [a.id, a]));
  const antennaShortName = (id: string) => antennaLookup.get(id)?.shortName ?? id;

  const conflicts: Conflict[] = [];
  const resolutions: Resolution[] = [];
  const resolutionContext: ResolutionContext = {
    missionLookup,
    antennas,
    antennaLookup,
    antennaShortName,
    contactWindows,
    allocations: sweep.allocations,
    antennaSchedule: sweep.antennaSchedule,
    capacityOf,
    recoverableByMission: new Map(),
    allowedByMission: new Map(),
    disjointWindows: Object.values(contactWindows).every((byAntenna) =>
      Object.values(byAntenna).every((windows) =>
        windows.every((w, i) => i === 0 || windows[i - 1].endMs <= w.startMs),
      ),
    ),
  };
  for (const detail of sweep.conflicts) {
    const id = 'conflict_' + conflicts.length;
    conflicts.push(buildConflictRecord(id, detail));
    resolutions.push(resolveConflict(id, detail, resolutionContext));
  }

  // Step 7: mission SLA (iterated in priority order).
  const missionSLA = calculateMissionSla(
    sortedMissions,
    sweep.allocations,
    antennas,
    startMs,
    endMs,
    isToggleEnabled,
  );

  // Step 8: antenna utilisation.
  const antennaUtil = calculateAntennaUtil(antennas, sweep.antennaSchedule, startMs, endMs);

  // Step 9: summary.
  const resolvedCount = resolutions.filter((r) => r.strategy !== RESOLUTION.UNRESOLVED).length;
  const slaKeys = Object.keys(missionSLA);
  const avgSLA =
    slaKeys.length > 0
      ? Math.round(slaKeys.reduce((sum, key) => sum + missionSLA[key].percentage, 0) / slaKeys.length)
      : 0;

  const summary: AnalysisSummary = {
    missionCount: missions.length,
    antennaCount: antennas.length,
    totalConflicts: conflicts.length,
    resolvedConflicts: resolvedCount,
    unresolvedConflicts: conflicts.length - resolvedCount,
    overallSLA: avgSLA,
    analysisTimeMs: input.nowMs(),
    timeframeStartMs: startMs,
    timeframeEndMs: endMs,
  };

  // Step 10: recommendations (toggle auto_recommendations).
  const recommendations = isToggleEnabled('auto_recommendations')
    ? generateRecommendations(missionSLA, missions, antennas, antennaUtil)
    : [];

  // Step 11: saturation (toggle network_saturation).
  const saturation = isToggleEnabled('network_saturation')
    ? calculateNetworkSaturation(missionSLA, antennaUtil, antennaShortName)
    : null;

  return {
    allocations: sweep.allocations,
    conflicts,
    resolutions,
    missionSLA,
    antennaUtil,
    summary,
    contactWindows,
    recommendations,
    saturation,
  };
}

/**
 * Step 3: shorten or remove windows on antennas with a live weather impact
 * above 0.05. Optical antennas lose every window at a factor of 0.80 or more;
 * otherwise a factor of 0.15 or more scales each duration by (1 - factor).
 */
export function applyWeatherDegradation(
  contactWindows: ContactWindowMap,
  antennas: Antenna[],
  impacts: Record<string, WeatherImpactInput>,
): void {
  for (const antenna of antennas) {
    const impact = impacts[antenna.id];
    if (!impact || impact.source === 'static') continue;

    const factor = impact.impactFactor || 0;
    if (factor <= 0.05) continue;

    const isOptical = antenna.capabilities.bands.indexOf('Optical') !== -1;

    for (const missionId of Object.keys(contactWindows)) {
      const windows = contactWindows[missionId][antenna.id];
      if (!windows || windows.length === 0) continue;

      if (isOptical && factor >= 0.8) {
        contactWindows[missionId][antenna.id] = [];
      } else if (factor >= 0.15) {
        const degraded: ContactWindow[] = [];
        for (const w of windows) {
          const degradedDuration = Math.round(w.durationMin * (1 - factor));
          if (degradedDuration < 1) continue;
          degraded.push({
            ...w,
            endMs: w.startMs + degradedDuration * MINUTE_MS,
            durationMin: degradedDuration,
            weatherDegraded: true,
          });
        }
        contactWindows[missionId][antenna.id] = degraded;
      }
    }
  }
}

/**
 * Step 4: an antenna with maintenanceWindowHrs > 0 is in maintenance from
 * 00:00 UTC for that many hours; windows whose UTC start hour falls inside are
 * dropped. (The original's two-clause test reduces to startHour < maintHrs.)
 */
export function applyMaintenanceBlocking(contactWindows: ContactWindowMap, antennas: Antenna[]): void {
  for (const antenna of antennas) {
    const maintHrs = antenna.capabilities.maintenanceWindowHrs || 0;
    if (maintHrs <= 0) continue;

    for (const missionId of Object.keys(contactWindows)) {
      const windows = contactWindows[missionId] && contactWindows[missionId][antenna.id];
      if (!windows) continue;
      contactWindows[missionId][antenna.id] = windows.filter(
        (w) => new Date(w.startMs).getUTCHours() >= maintHrs,
      );
    }
  }
}

/** Step 5: drop windows shorter than the mission's minPassDurationMin. */
export function applyMinimumPass(
  contactWindows: ContactWindowMap,
  missions: Mission[],
  antennas: Antenna[],
): void {
  for (const mission of missions) {
    const minDuration = mission.requirements.minPassDurationMin || 0;
    if (minDuration <= 0) continue;
    const missionWindows = contactWindows[mission.id];
    if (!missionWindows) continue;

    for (const antenna of antennas) {
      const windows = missionWindows[antenna.id];
      if (!windows) continue;
      missionWindows[antenna.id] = windows.filter((w) => w.durationMin >= minDuration);
    }
  }
}

/**
 * Simultaneous contacts an antenna can carry: 1, raised to simultaneousContacts
 * under the phased_array toggle and to mspaDownlink under mspa_dsn.
 */
export function getAntennaCapacity(antenna: Antenna, isToggleEnabled: (id: string) => boolean): number {
  let capacity = 1;
  if (isToggleEnabled('phased_array')) {
    capacity = Math.max(capacity, antenna.capabilities?.simultaneousContacts || 1);
  }
  if (isToggleEnabled('mspa_dsn')) {
    capacity = Math.max(capacity, antenna.capabilities?.mspaDownlink || 1);
  }
  return capacity;
}

/** Lower is better: index in preferredAntennas, 100 + index in acceptableAlternatives, else 1000. */
export function getAntennaPreferenceRank(mission: Mission, antennaId: string): number {
  const preferred = mission.preferredAntennas || [];
  for (let i = 0; i < preferred.length; i++) {
    if (preferred[i] === antennaId) return i;
  }
  const alternatives = mission.acceptableAlternatives || [];
  for (let j = 0; j < alternatives.length; j++) {
    if (alternatives[j] === antennaId) return 100 + j;
  }
  return 1000;
}

export interface TimeInterval {
  startMs: number;
  endMs: number;
}

/** A blocked mission pair on one antenna, aggregated over the whole timeframe. */
export interface ConflictDetail {
  antennaId: string;
  winnerId: string;
  loserId: string;
  /** First blocked segment start. */
  startMs: number;
  /** Latest blocked segment end. */
  endMs: number;
  /** Sum of the blocked segment lengths. */
  durationMs: number;
  segments: number;
  /** Blocked segments, contiguous ones merged. */
  intervals: TimeInterval[];
}

export interface SweepResult {
  allocations: AllocationMap;
  antennaSchedule: AntennaSchedule;
  conflicts: ConflictDetail[];
}

interface SweepEntry {
  missionId: string;
  priority: number;
  prefRank: number;
  startMs: number;
  endMs: number;
  maxElevation: number;
}

interface SweepEvent {
  t: number;
  /** 1 = window start, 0 = window end; ends sort before starts at equal times. */
  type: 0 | 1;
  idx: number;
}

interface OpenAllocation {
  startMs: number;
  maxElevation: number;
}

interface ConflictAggregate {
  missionA: string;
  missionB: string;
  startMs: number;
  endMs: number;
  durationMs: number;
  segments: number;
  intervals: TimeInterval[];
}

function compareCandidates(a: SweepEntry, b: SweepEntry): number {
  if (a.priority !== b.priority) return a.priority - b.priority;
  if (a.prefRank !== b.prefRank) return a.prefRank - b.prefRank;
  if (a.startMs !== b.startMs) return a.startMs - b.startMs;
  if (a.endMs !== b.endMs) return a.endMs - b.endMs;
  if (a.missionId < b.missionId) return -1;
  if (a.missionId > b.missionId) return 1;
  return 0;
}

/**
 * Step 6: for each antenna independently, sweep the window start and end
 * events in time order. Between consecutive event times the active windows are
 * ranked by (priority, preference rank, start, end, mission id) and the first
 * `capacity` of them hold the antenna; a mission's consecutive winning
 * segments merge into one allocation. Every loser in an over-subscribed
 * segment is aggregated into a conflict keyed loser|primary winner.
 */
export function allocateByAntennaSweep(
  missions: Mission[],
  antennas: Antenna[],
  contactWindows: ContactWindowMap,
  capacityOf: (antenna: Antenna) => number,
): SweepResult {
  const allocations: AllocationMap = {};
  const antennaSchedule: AntennaSchedule = {};
  for (const mission of missions) {
    allocations[mission.id] = {};
    for (const antenna of antennas) allocations[mission.id][antenna.id] = [];
  }
  for (const antenna of antennas) antennaSchedule[antenna.id] = [];

  const conflicts: ConflictDetail[] = [];

  for (const antenna of antennas) {
    const capacity = capacityOf(antenna);
    const entries: SweepEntry[] = [];

    for (const mission of missions) {
      const missionWindows = contactWindows[mission.id];
      if (!missionWindows) continue;
      const windows = missionWindows[antenna.id];
      if (!windows || windows.length === 0) continue;

      const prefRank = getAntennaPreferenceRank(mission, antenna.id);
      const priority = mission.priorityTier || 99;

      for (const w of windows) {
        if (w.endMs <= w.startMs) continue;
        entries.push({
          missionId: mission.id,
          priority,
          prefRank,
          startMs: w.startMs,
          endMs: w.endMs,
          maxElevation: w.maxElevation || 0,
        });
      }
    }

    if (entries.length === 0) continue;

    const events: SweepEvent[] = [];
    for (let i = 0; i < entries.length; i++) {
      events.push({ t: entries[i].startMs, type: 1, idx: i });
      events.push({ t: entries[i].endMs, type: 0, idx: i });
    }
    events.sort((a, b) => (a.t !== b.t ? a.t - b.t : a.type - b.type));

    const active = new Uint8Array(entries.length);
    const activeIdx: number[] = [];
    const openByMission = new Map<string, OpenAllocation>();
    const conflictAgg = new Map<string, ConflictAggregate>();

    const closeMissionAllocation = (missionId: string, closeMs: number) => {
      const open = openByMission.get(missionId);
      if (!open) return;
      if (closeMs <= open.startMs) {
        openByMission.delete(missionId);
        return;
      }
      const durationMin = Math.round((closeMs - open.startMs) / MINUTE_MS);
      if (durationMin > 0) {
        const allocation: Allocation = {
          startMs: open.startMs,
          endMs: closeMs,
          maxElevation: open.maxElevation,
          durationMin,
        };
        allocations[missionId][antenna.id].push(allocation);
        antennaSchedule[antenna.id].push({ ...allocation, missionId });
      }
      openByMission.delete(missionId);
    };

    const processSegment = (segStart: number, segEnd: number) => {
      if (segEnd <= segStart) return;

      const activeEntries: SweepEntry[] = [];
      for (const idx of activeIdx) {
        if (active[idx]) activeEntries.push(entries[idx]);
      }

      if (activeEntries.length === 0) {
        for (const openMission of Array.from(openByMission.keys())) {
          closeMissionAllocation(openMission, segStart);
        }
        return;
      }

      activeEntries.sort(compareCandidates);

      const winners = new Set<string>();
      const winnerList: SweepEntry[] = [];
      for (let wi = 0; wi < activeEntries.length && wi < capacity; wi++) {
        winners.add(activeEntries[wi].missionId);
        winnerList.push(activeEntries[wi]);
      }

      for (const currentMission of Array.from(openByMission.keys())) {
        if (!winners.has(currentMission)) closeMissionAllocation(currentMission, segStart);
      }

      for (const winner of winnerList) {
        const open = openByMission.get(winner.missionId);
        if (!open) {
          openByMission.set(winner.missionId, { startMs: segStart, maxElevation: winner.maxElevation });
        } else if (winner.maxElevation > open.maxElevation) {
          open.maxElevation = winner.maxElevation;
        }
      }

      if (activeEntries.length > capacity) {
        const primaryWinner = winnerList[0];
        for (let li = capacity; li < activeEntries.length; li++) {
          const loser = activeEntries[li];
          if (loser.missionId === primaryWinner.missionId) continue;

          const key = loser.missionId + '|' + primaryWinner.missionId;
          const agg = conflictAgg.get(key);
          if (!agg) {
            conflictAgg.set(key, {
              missionA: primaryWinner.missionId,
              missionB: loser.missionId,
              startMs: segStart,
              endMs: segEnd,
              durationMs: segEnd - segStart,
              segments: 1,
              intervals: [{ startMs: segStart, endMs: segEnd }],
            });
          } else {
            agg.endMs = segEnd;
            agg.durationMs += segEnd - segStart;
            agg.segments += 1;
            const last = agg.intervals[agg.intervals.length - 1];
            if (last.endMs === segStart) last.endMs = segEnd;
            else agg.intervals.push({ startMs: segStart, endMs: segEnd });
          }
        }
      }
    };

    let prevT = events[0].t;
    let ei = 0;
    while (ei < events.length) {
      const t = events[ei].t;
      processSegment(prevT, t);

      while (ei < events.length && events[ei].t === t) {
        const e = events[ei];
        if (e.type === 1) {
          if (!active[e.idx]) {
            active[e.idx] = 1;
            activeIdx.push(e.idx);
          }
        } else {
          active[e.idx] = 0;
        }
        ei++;
      }

      prevT = t;
    }

    for (const finalMission of Array.from(openByMission.keys())) {
      closeMissionAllocation(finalMission, prevT);
    }

    for (const agg of conflictAgg.values()) {
      conflicts.push({
        antennaId: antenna.id,
        winnerId: agg.missionA,
        loserId: agg.missionB,
        startMs: agg.startMs,
        endMs: agg.endMs,
        durationMs: agg.durationMs,
        segments: agg.segments,
        intervals: agg.intervals,
      });
    }
  }

  return { allocations, antennaSchedule, conflicts };
}

function buildConflictRecord(id: string, detail: ConflictDetail): Conflict {
  const blockedMin = Math.round(detail.durationMs / MINUTE_MS);
  return {
    id,
    missionA: detail.winnerId,
    missionB: detail.loserId,
    antennaId: detail.antennaId,
    window: { startMs: detail.startMs, endMs: detail.endMs, maxElevation: 0, durationMin: blockedMin },
    overlapStartMs: detail.startMs,
    overlapEndMs: detail.endMs,
    timeMs: detail.startMs,
    blockedMinutes: blockedMin,
    segments: detail.segments,
  };
}

export interface ResolutionContext {
  missionLookup: Map<string, Mission>;
  antennas: Antenna[];
  antennaLookup: Map<string, Antenna>;
  antennaShortName: (id: string) => string;
  contactWindows: ContactWindowMap;
  allocations: AllocationMap;
  antennaSchedule: AntennaSchedule;
  capacityOf: (antenna: Antenna) => number;
  /** Per-analysis indexes; never shared across runs, weather changes or what-ifs. */
  recoverableByMission?: Map<string, Map<string, RecoverableTime>>;
  allowedByMission?: Map<string, Antenna[]>;
  disjointWindows?: boolean;
}

function overlapMs(a: TimeInterval, b: TimeInterval): number {
  return Math.max(0, Math.min(a.endMs, b.endMs) - Math.max(a.startMs, b.startMs));
}

function roundRatio(numerator: number, denominator: number): number {
  if (denominator <= 0) return 0;
  return Math.round(Math.min(1, numerator / denominator) * 1000) / 1000;
}

/**
 * Antennas the loser may use: preferredAntennas then acceptableAlternatives
 * (active and band-compatible only); every active band-compatible antenna
 * when both lists are empty, as the synthetic-window generator treats them.
 */
function allowedAntennas(mission: Mission, ctx: ResolutionContext): Antenna[] {
  const band = mission.requirements.bandType;
  const compatible = (antenna: Antenna) => antenna.capabilities.bands.some((b) => b === band);
  const listed = (mission.preferredAntennas || []).concat(mission.acceptableAlternatives || []);
  if (listed.length === 0) return ctx.antennas.filter(compatible);
  const result: Antenna[] = [];
  for (const id of listed) {
    const antenna = ctx.antennaLookup.get(id);
    if (antenna && compatible(antenna) && !result.includes(antenna)) result.push(antenna);
  }
  return result;
}

/**
 * Resolution per conflict, tried in order:
 * (a) MOVE_TO_ALTERNATE: another allowed antenna has a window for the loser
 *     overlapping the blocked segments with spare capacity (the loser's own
 *     allocation there counts as captured); dataCapture = recoverable / blocked.
 * (b) ACCEPT_REDUCED: the loser still holds at least minPassDurationMin of a
 *     blocked pass on this antenna; dataCapture = allocated / pass minutes.
 * (c) BUMP_LOWER: the winner outranks the loser.
 * (d) WAIT_NEXT_PASS: the loser has a later window on an allowed antenna.
 * (e) UNRESOLVED.
 */
export function resolveConflict(
  conflictId: string,
  detail: ConflictDetail,
  ctx: ResolutionContext,
): Resolution {
  const loser = ctx.missionLookup.get(detail.loserId);
  const winner = ctx.missionLookup.get(detail.winnerId);
  const loserName = loser ? loser.shortName : detail.loserId;
  const antennaName = ctx.antennaShortName(detail.antennaId);
  const blockedMin = Math.round(detail.durationMs / MINUTE_MS);

  const base = {
    conflictId,
    missionId: detail.loserId,
    originalAntennaId: detail.antennaId,
    newAntennaId: null as string | null,
    dataCapture: 0,
  };
  const deferred = (strategy: ResolutionStrategy): Resolution => ({
    ...base,
    strategy,
    description:
      loserName +
      ' deferred by higher-priority traffic on ' +
      antennaName +
      ' (' +
      blockedMin +
      ' min blocked)',
  });

  if (!loser) {
    // The loser is not an analysed mission; only the tier rule applies, as in the original.
    const winnerTier = winner ? winner.priorityTier : 99;
    return deferred(winnerTier < 99 ? RESOLUTION.BUMP_LOWER : RESOLUTION.WAIT_NEXT_PASS);
  }

  const allowedCache = (ctx.allowedByMission ??= new Map());
  let allowed = allowedCache.get(loser.id);
  if (!allowed) {
    allowed = allowedAntennas(loser, ctx);
    allowedCache.set(loser.id, allowed);
  }
  const loserWindows = ctx.contactWindows[loser.id] || {};
  const indexCache = (ctx.recoverableByMission ??= new Map());
  let indexes = indexCache.get(loser.id);
  if (!indexes) {
    indexes = new Map();
    indexCache.set(loser.id, indexes);
  }

  // (a) Move to an alternate antenna.
  let bestAlternate: Antenna | null = null;
  let bestFreeMs = 0;
  const maximumRecovery = detail.intervals.reduce((sum, w) => sum + w.endMs - w.startMs, 0);
  for (const alternate of allowed) {
    if (alternate.id === detail.antennaId) continue;
    const windows = loserWindows[alternate.id];
    if (!windows || windows.length === 0) continue;
    let recoverable = indexes.get(alternate.id);
    if (!recoverable) {
      recoverable = indexRecoverableTime(
        windows,
        ctx.antennaSchedule[alternate.id] || [],
        ctx.capacityOf(alternate),
        loser.id,
      );
      indexes.set(alternate.id, recoverable);
    }
    let freeMs = 0;
    for (const blocked of detail.intervals) {
      freeMs += recoverable(blocked);
    }
    if (freeMs > bestFreeMs) {
      bestFreeMs = freeMs;
      bestAlternate = alternate;
      // With non-overlapping windows no later antenna can exceed full recovery.
      // The original strict-greater tie rule already favours this first antenna.
      if (ctx.disjointWindows && bestFreeMs === maximumRecovery) break;
    }
  }
  if (bestAlternate) {
    const recoveredMin = Math.round(bestFreeMs / MINUTE_MS);
    return {
      ...base,
      strategy: RESOLUTION.MOVE_TO_ALTERNATE,
      newAntennaId: bestAlternate.id,
      dataCapture: roundRatio(bestFreeMs, detail.durationMs),
      description:
        loserName +
        ' moved to ' +
        ctx.antennaShortName(bestAlternate.id) +
        ': ' +
        recoveredMin +
        ' of ' +
        blockedMin +
        ' min blocked on ' +
        antennaName +
        ' recoverable',
    };
  }

  // (b) Accept the reduced pass on this antenna.
  const minPassMin = loser.requirements.minPassDurationMin || 0;
  const passes = (loserWindows[detail.antennaId] || []).filter((w) =>
    detail.intervals.some((blocked) => overlapMs(blocked, w) > 0),
  );
  if (passes.length > 0) {
    const allocated = ctx.allocations[loser.id]?.[detail.antennaId] || [];
    let allocatedMs = 0;
    let passMs = 0;
    let qualifies = false;
    for (const pass of passes) {
      let inPassMs = 0;
      for (const a of allocated) inPassMs += overlapMs(a, pass);
      passMs += pass.endMs - pass.startMs;
      allocatedMs += inPassMs;
      if (inPassMs > 0 && inPassMs / MINUTE_MS >= minPassMin) qualifies = true;
    }
    if (qualifies) {
      return {
        ...base,
        strategy: RESOLUTION.ACCEPT_REDUCED,
        dataCapture: roundRatio(allocatedMs, passMs),
        description:
          loserName +
          ' keeps ' +
          Math.round(allocatedMs / MINUTE_MS) +
          ' of ' +
          Math.round(passMs / MINUTE_MS) +
          ' min on ' +
          antennaName +
          ' (reduced pass)',
      };
    }
  }

  // (c) The winner outranks the loser.
  const winnerTier = winner ? winner.priorityTier : 99;
  if (winnerTier < loser.priorityTier) return deferred(RESOLUTION.BUMP_LOWER);

  // (d) A later pass exists on an allowed antenna.
  const blockedEnd = detail.intervals[detail.intervals.length - 1].endMs;
  for (const antenna of allowed) {
    const windows = loserWindows[antenna.id];
    if (windows && windows.some((w) => w.startMs >= blockedEnd)) return deferred(RESOLUTION.WAIT_NEXT_PASS);
  }

  // (e) Nothing recovers the contact.
  return {
    ...base,
    strategy: RESOLUTION.UNRESOLVED,
    description:
      loserName +
      ' loses ' +
      blockedMin +
      ' min on ' +
      antennaName +
      ': no alternate antenna, reduced pass or later pass available',
  };
}

/** Step 7: SLA per mission, requirement scaled from minutesPerYear to the timeframe. */
export function calculateMissionSla(
  missions: Mission[],
  allocations: AllocationMap,
  antennas: Antenna[],
  startMs: number,
  endMs: number,
  isToggleEnabled: (id: string) => boolean,
): Record<string, MissionSla> {
  const sla: Record<string, MissionSla> = {};
  const hoursInRange = (endMs - startMs) / HOUR_MS;
  const daysInRange = hoursInRange / 24;
  const dataRateSla = isToggleEnabled('data_rate_sla');
  const missionThresholds = isToggleEnabled('mission_sla_thresholds');

  for (const mission of missions) {
    let totalAllocatedMin = 0;
    let windowCount = 0;
    const perAntenna: Record<string, { minutes: number; passes: number }> = {};

    for (const antenna of antennas) {
      const windows = allocations[mission.id] && allocations[mission.id][antenna.id];
      if (!windows) {
        perAntenna[antenna.id] = { minutes: 0, passes: 0 };
        continue;
      }
      let antennaMin = 0;
      for (const w of windows) antennaMin += w.durationMin;
      perAntenna[antenna.id] = { minutes: antennaMin, passes: windows.length };
      totalAllocatedMin += antennaMin;
      windowCount += windows.length;
    }

    const requiredMinPerYear = mission.requirements.minutesPerYear || 0;
    const scaledRequiredMin = requiredMinPerYear * (daysInRange / 365);

    let percentage = 0;
    if (scaledRequiredMin > 0) {
      percentage = Math.min(100, Math.round((totalAllocatedMin / scaledRequiredMin) * 100));
    } else if (totalAllocatedMin > 0) {
      percentage = 100;
    }

    let dataSLA: DataSla | null = null;
    if (dataRateSla && mission.requirements.dataPerDayGB) {
      let totalDataGB = 0;
      for (const antenna of antennas) {
        const windows = allocations[mission.id] && allocations[mission.id][antenna.id];
        if (!windows) continue;
        const dataRateMbps = estimateDataRateMbps(antenna, mission.requirements.bandType);
        for (const w of windows) totalDataGB += (w.durationMin * dataRateMbps * 60) / 8000;
      }
      const requiredGB = mission.requirements.dataPerDayGB * daysInRange;
      const dataPct = requiredGB > 0 ? Math.min(100, Math.round((totalDataGB / requiredGB) * 100)) : 100;
      dataSLA = {
        deliveredGB: Math.round(totalDataGB * 100) / 100,
        requiredGB: Math.round(requiredGB * 100) / 100,
        percentage: dataPct,
        status: getSlaStatus(dataPct, mission, missionThresholds),
      };
    }

    sla[mission.id] = {
      totalMinutes: totalAllocatedMin,
      requiredMinutes: Math.round(scaledRequiredMin),
      passes: windowCount,
      percentage,
      status: getSlaStatus(percentage, mission, missionThresholds),
      perAntenna,
      dataSLA,
    };
  }

  return sla;
}

/** Downlink rate estimate (Mbps) by band; large apertures (over 30 m) get the higher S/X/Ka figure. */
export function estimateDataRateMbps(antenna: Antenna, bandType: string | undefined): number {
  const diamM = antenna.capabilities ? antenna.capabilities.diameterM : 10;
  const large = diamM !== undefined && diamM > 30;
  switch (bandType) {
    case 'S-band':
      return large ? 10 : 2;
    case 'X-band':
      return large ? 150 : 50;
    case 'Ka-band':
      return large ? 600 : 200;
    case 'Optical':
      return 1200;
    case 'Ku-band':
      return 100;
    case 'UHF':
      return 1;
    case 'EHF':
      return 8;
    default:
      return 5;
  }
}

/**
 * met / partial / unmet against the mission's slaMinimumPercent (partial at
 * 75% of it) when mission_sla_thresholds is on, else the network 95 / 70.
 */
export function getSlaStatus(
  pct: number,
  mission: Mission | undefined,
  missionThresholds: boolean,
): SlaStatus {
  if (missionThresholds && mission && mission.requirements && mission.requirements.slaMinimumPercent) {
    const threshold = mission.requirements.slaMinimumPercent;
    if (pct >= threshold) return 'met';
    if (pct >= threshold * 0.75) return 'partial';
    return 'unmet';
  }
  if (pct >= ANALYSIS.SLA_MET_THRESHOLD) return 'met';
  if (pct >= ANALYSIS.SLA_PARTIAL_THRESHOLD) return 'partial';
  return 'unmet';
}

/** Step 8: scheduled minutes against the maintenance-adjusted daily allowance. */
export function calculateAntennaUtil(
  antennas: Antenna[],
  antennaSchedule: AntennaSchedule,
  startMs: number,
  endMs: number,
): Record<string, AntennaUtil> {
  const util: Record<string, AntennaUtil> = {};
  const hoursInRange = (endMs - startMs) / HOUR_MS;

  for (const antenna of antennas) {
    const schedule = antennaSchedule[antenna.id] || [];
    let totalScheduledMin = 0;
    for (const entry of schedule) totalScheduledMin += (entry.endMs - entry.startMs) / MINUTE_MS;

    const effectiveHrsPerDay = antenna.capabilities.maxHoursPerDay || ANALYSIS.MAX_HOURS_PER_DAY;
    const maintenanceHrs = antenna.capabilities.maintenanceWindowHrs || 0;
    const netHrsPerDay = Math.max(0, effectiveHrsPerDay - maintenanceHrs);
    const maxAvailableMin = hoursInRange * 60 * (netHrsPerDay / 24);
    let percentage = 0;
    if (maxAvailableMin > 0) {
      percentage = Math.min(100, Math.round((totalScheduledMin / maxAvailableMin) * 100));
    }

    const status: UtilStatus = percentage > 90 ? 'overloaded' : percentage > 70 ? 'high' : 'normal';
    util[antenna.id] = {
      scheduledMinutes: Math.round(totalScheduledMin),
      availableMinutes: Math.round(maxAvailableMin),
      percentage,
      status,
      passes: schedule.length,
    };
  }

  return util;
}

/**
 * Step 10: for every mission under 95% SLA, the least-utilised band-compatible
 * antenna outside its preferred list (below 80% utilisation); top 5 by score.
 */
export function generateRecommendations(
  missionSLA: Record<string, MissionSla>,
  missions: Mission[],
  antennas: Antenna[],
  antennaUtil: Record<string, AntennaUtil>,
): Recommendation[] {
  const recs: Recommendation[] = [];

  for (const missionId of Object.keys(missionSLA)) {
    const sla = missionSLA[missionId];
    if (sla.percentage >= 95) continue;

    const mission = missions.find((m) => m.id === missionId);
    if (!mission) continue;

    let bestAntenna: Antenna | null = null;
    let bestUtil = 100;
    for (const antenna of antennas) {
      if ((mission.preferredAntennas || []).indexOf(antenna.id) !== -1) continue;
      const compatible = antenna.capabilities.bands.some((b) => b === mission.requirements.bandType);
      if (!compatible) continue;
      const util = antennaUtil[antenna.id] ? antennaUtil[antenna.id].percentage : 0;
      if (util < bestUtil) {
        bestUtil = util;
        bestAntenna = antenna;
      }
    }

    if (bestAntenna && bestUtil < 80) {
      const estimatedImprovement = Math.min(100 - sla.percentage, Math.round((100 - bestUtil) * 0.3));
      recs.push({
        missionId,
        missionName: mission.shortName,
        missionShortName: mission.shortName,
        currentSLA: sla.percentage,
        suggestion: 'Add ' + bestAntenna.shortName + ' as alternative for ' + mission.shortName,
        antennaId: bestAntenna.id,
        antennaName: bestAntenna.shortName,
        antennaShortName: bestAntenna.shortName,
        currentUtil: bestUtil,
        estimatedImprovement,
        score: 100 - sla.percentage + estimatedImprovement,
      });
    }
  }

  return recs.sort((a, b) => b.score - a.score).slice(0, 5);
}

/** Step 11: network saturation; null when there are no missions. */
export function calculateNetworkSaturation(
  missionSLA: Record<string, MissionSla>,
  antennaUtil: Record<string, AntennaUtil>,
  antennaShortName: (id: string) => string,
): Saturation | null {
  const slaValues = Object.keys(missionSLA).map((k) => missionSLA[k].percentage);
  if (slaValues.length === 0) return null;

  const avgSLA = Math.round(slaValues.reduce((a, b) => a + b, 0) / slaValues.length);
  const minSLA = Math.min(...slaValues);

  let bottleneck: string | null = null;
  let maxUtil = 0;
  for (const id of Object.keys(antennaUtil)) {
    if (antennaUtil[id].percentage > maxUtil) {
      maxUtil = antennaUtil[id].percentage;
      bottleneck = id;
    }
  }

  // maxUtil > 90 implies a bottleneck id was recorded.
  const recommendation =
    maxUtil > 90 && bottleneck !== null
      ? 'Adding capacity at ' +
        antennaShortName(bottleneck) +
        ' would improve network SLA by ~' +
        Math.round((100 - avgSLA) * 0.4) +
        '%'
      : 'Network capacity is adequate';

  return {
    averageSLA: avgSLA,
    minimumSLA: Math.round(minSLA),
    bottleneckAntenna: bottleneck,
    bottleneckUtil: maxUtil,
    missionsAboveTarget: slaValues.filter((v) => v >= 95).length,
    totalMissions: slaValues.length,
    recommendation,
  };
}
